import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { partners as fallbackPartners } from "../mock";
import { useContent, renderHighlightedTitle } from "../lib/useContent";

export const CTA = () => {
  const { field } = useContent("home");
  const pretitle = field("cta_final", "pretitle", "Diventa parte del CCN");
  const headline = field("cta_final", "headline", "Fai crescere la tua attività insieme al paese.");
  const highlightTerms = field("cta_final", "highlight_terms", ["insieme al paese"]);
  const description = field("cta_final", "description", "Associati al Centro Commerciale Naturale: più visibilità, eventi condivisi, formazione e una rete di colleghi che lavorano per la stessa comunità.");
  const primaryLabel = field("cta_final", "primary_cta_label", "Associati ora");
  const primaryUrl = field("cta_final", "primary_cta_url", "/diventa-socio");
  const secondaryLabel = field("cta_final", "secondary_cta_label", "Scopri di più");
  const secondaryUrl = field("cta_final", "secondary_cta_url", "/chi-siamo");

  return (
    <section className="py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[36px] bg-stone-950 text-white p-10 md:p-20">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full" style={{background: "radial-gradient(circle, rgba(18,150,74,0.7) 0%, rgba(18,150,74,0) 70%)"}} />
          <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full" style={{background: "radial-gradient(circle, rgba(111,194,71,0.5) 0%, rgba(111,194,71,0) 70%)"}} />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              {pretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-stone-400 mb-4">{pretitle}</div>}
              <h3 className="font-display text-4xl md:text-6xl leading-[1.05]">
                {renderHighlightedTitle(headline, highlightTerms)}
              </h3>
              {description !== "" && <p className="mt-6 text-stone-300 text-lg max-w-2xl">{description}</p>}
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
              {primaryLabel !== "" && (
                <Link
                  to={primaryUrl}
                  className="group inline-flex items-center justify-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-7 py-4 transition-colors"
                  data-testid="cta-primary"
                >
                  {primaryLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
              {secondaryLabel !== "" && (
                <Link
                  to={secondaryUrl}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white rounded-full px-7 py-4 transition-colors"
                  data-testid="cta-secondary"
                >
                  {secondaryLabel}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PartnersMarquee = () => {
  const { field } = useContent("home");
  const label = field("partners", "label", "Con il supporto di");
  const itemsRaw = field("partners", "items", null);
  const items = Array.isArray(itemsRaw) && itemsRaw.length
    ? itemsRaw.filter((p) => p.visible !== false).map((p) => p.name).filter(Boolean)
    : fallbackPartners;

  if (items.length === 0) return null;

  return (
    <section className="py-16 border-y border-stone-200 bg-white/50 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-8">
        {label !== "" && (
          <div className="text-xs uppercase tracking-[0.22em] text-stone-500">
            {label}
          </div>
        )}
      </div>
      <div className="relative">
        <div className="marquee-track animate-marquee">
          {[...items, ...items].map((p, i) => (
            <div key={`${p}-${i}`} className="font-display text-2xl md:text-3xl text-stone-400 whitespace-nowrap">
              {p} <span className="mx-8 text-[#12964A]">•</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
